<script lang="ts">
  import type { Place, Maybe, Map, MapTextbox } from "../../types";
  import { fade } from "svelte/transition";
  import MapTextboxIcon from "./map-textbox-icon.svelte";
  import { isVisible } from "../../domain/map";
  import { getNow } from "../../data/clock";
  import { isDisplayablePlace } from "../../domain/map";

  export let time = getNow();
  export let zoom = 1;
  export let map: Map;
  export let textbox: MapTextbox;
  export let place: Maybe<Place> = null;
  export let linkBuilder = (_query: { place: string }) => "";
  export let activePlaceId: Maybe<string> = null;
  export let stroke: Maybe<string> = null;

  $: x = textbox.x;
  $: y = textbox.y;
  $: rotation = textbox.rotation;
  $: width = textbox.width;
  $: color = textbox.color || map.textColor;
  $: fontSize = textbox.fontSize;
  $: fontFamily = textbox.fontFamily || "inherit";
  $: fontWeight = textbox.fontWeight || "inherit";
  $: active = textbox.placeId && activePlaceId == textbox.placeId;
  $: highlightText = active && text;
  $: highlightIcon = active && !text;
  $: href = textbox.placeId ? linkBuilder({ place: textbox.placeId }) : null;
  $: text = textbox.text.trim();
  $: textAlign = textbox.textAlign;

  $: withinZoom = isVisible(zoom, textbox.zoomMin, textbox.zoomMax);
  $: display = place ? isDisplayablePlace(place, $time) : true;

  let fadeOptions = { duration: 150 };
</script>

{#if withinZoom && display}
  <div
    class="Wrapper"
    transition:fade={fadeOptions}
    style="transform: rotate({rotation}deg); left: {x}px; top: {y}px; width: {width}px;
           color: {color}; font-family: {fontFamily}; font-size: {fontSize}px; font-weight: {fontWeight}; --stroke: {stroke};
           text-align: {textAlign}"
  >
    <a class="Text" class:highlighted={highlightText} draggable="false" {href}>
      <MapTextboxIcon {textbox} isActive={!!highlightIcon} />

      {#if text}
        <span class="Label">
          {#if stroke}
            <span class="Stroke" aria-hidden="true">
              {text}
            </span>
          {/if}
          {text}
        </span>
      {/if}
    </a>
  </div>
{/if}

<style>
  .Wrapper {
    letter-spacing: 0;
    line-height: 1.16; /* comes from fabricjs textboxes */
    transform-origin: 0 0;
    position: absolute;
    hyphens: none;
  }

  .Text {
    border-radius: 24px;
    color: inherit;
    display: inline-block;
    position: relative;
    min-height: 1.16em; /* comes from fabricjs textboxes */
    text-decoration: none;
    -webkit-touch-callout: none; /* Prevent map previews in Safari */
    -webkit-tap-highlight-color: none; /* Prevent highlight flicker on click */
  }

  .Text.highlighted {
    background: rgba(0, 0, 0, 0.14);
    box-shadow: 0 0 0 4px rgba(0, 0, 0, 0.14);
  }

  .Text:hover,
  .Text:focus {
    text-decoration: none;
  }

  .Label {
    display: block;
    position: relative;
    z-index: 1;
  }

  .Stroke {
    color: transparent;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    -webkit-text-stroke: 2px var(--stroke);
    paint-order: stroke fill;
    user-select: none;
    z-index: -1;
  }
</style>
